// Angular
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
// Model
import { ORDER_STATE, RULE_MESSAGE_TYPE, RULE_STATUS, RULE_UPDATED_EVENT, RULESET_NAMES } from './model/constants';
import { RuleCommentModel, RuleModel, RuleStipModel } from './model/rule.model';
import { OrderDetailModel } from '../state/model/order-detail.model';
import { PostRevisionModel } from './model/post-revision.model';
import { PostRevisionBadge } from './model/post-revision-badge.model';
// Service
import { RuleService } from './service/rule.service';
import { BroadcastService } from '@cv-invision/core';
// State
import { Store } from '@ngxs/store';
import { QualityState } from '../state/quality.state';
// Util
import { buildPostRevisionBadges } from './util/post-revision-badges.util';
// Other
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { decodeHtmlEntities } from './util/post-revision-html-decode.util';

@Component({
	selector: 'cv-rule-component',
	templateUrl: './rule.component.html',
	styleUrls: ['./rule.component.scss']
})
export class RuleComponent implements OnInit, AfterViewInit, OnDestroy {
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	@Input() rule: RuleModel;
	@Input() status: RULE_STATUS;
	@Input() active: boolean;
	@Input() disabledActions: boolean;
	@Input() viewOnlyUncheckedRules: boolean;
	@Output() selected: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() viewComments: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() viewStips: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() addComment: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() addStip: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() updateStatus: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('commentRef') commentRef: ElementRef;
	@ViewChild('shadowRef') shadowRef!: ElementRef;
	private atComponentDestroy = new Subject();
	public RULE_STATUS = RULE_STATUS;
	public RULE_MESSAGE_TYPE = RULE_MESSAGE_TYPE;
	public type: RULE_MESSAGE_TYPE;
	public title: string;
	public description: string;
	public ruleSet: string;
	public category: string;
	resolver: any;
	public notes: RuleCommentModel[];
	public stips: RuleStipModel[];
	public orderDetailModel: OrderDetailModel;
	public updating_status: RULE_STATUS;
	public hideRule = false;
	public ruleId: string;
	public ruleClass: string;
	public ruleStatus: RULE_STATUS;
	public isAI = false;
	public isFeedbackOpen = false;
	public showToggle = false;
	public isExpanded = false;
	public shouldClamp = false;
	readonly CLAMP_HEIGHT = 34;
	public isPostRevision = false;
	postRevisionBadges: PostRevisionBadge[] = [];
	aiConfidenceScore?: number;
	postRevisionPanelCollapsed = true;
	isPostRevisionLoading = false;
	postRevisionDetails?: PostRevisionModel;
	postRevisionLoadError = false;

	get vendorNote() {
		return this.rule?.notes?.find(n => n.vendor === true);
	}

	get commentCount(): number {
		return this.rule?.notes?.filter(n => !n.vendor).length
	}

	/**
	 * Determines whether rule-level actions (e.g., add comment, add stip, update status)
	 * should be disabled.
	 *
	 * Actions are disabled when:
	 * - Global rule actions are disabled, or
	 * - The order is in POST_REVISION_RECEIVED state and the rule does not belong
	 *   to the POST_REVISION ruleset.
	 *
	 * In all other cases, actions remain enabled.
	 */
	get disableRuleActions(): boolean {
		if (this.disabledActions) return true;

		const isPostRevisionReceived =
			this.orderDetailModel?.stateEnum === ORDER_STATE.POST_REVISION_RECEIVED;

		const isPostRevisionRule =
			this.rule?.ruleSetName === RULESET_NAMES.POST_REVISION;

		if (isPostRevisionReceived) {
			return !isPostRevisionRule;
		}

		return false;
	}

	constructor(
		private ruleService: RuleService,
		private store: Store,
		private broadcastService: BroadcastService
	) {
		this.orderDetail$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((orderDetailModel: OrderDetailModel) => {
				this.orderDetailModel = orderDetailModel;
			});
		this.rule = {} as any;
	}
	ngAfterViewInit(): void {
		// wait for full rendering & styles to apply
		setTimeout(() => {
			const el = this.commentRef?.nativeElement;
			if (!el) return;

			const scrollHeight = el.scrollHeight;

			this.shouldClamp = scrollHeight - this.CLAMP_HEIGHT > 2;
		}, 0);
	}

	ngOnInit(): void {
		// Replace with endpoint property
		this.isPostRevision = this.rule.ruleSetName === RULESET_NAMES.POST_REVISION;
		this.type = this.rule.messageType || RULE_MESSAGE_TYPE.Info;
		this.title = this.rule.displayText;
		this.description = this.rule.message;
		this.ruleSet = this.rule.ruleSetDisplayText;
		this.category = this.rule.category;
		this.resolver = this.rule.resolver;
		this.notes = this.rule.notes || [];
		this.stips = this.rule.stips || [];
		this.ruleId = 'rule' + this.rule.uiSection + this.rule.displayText.replace(/\s/g, '');

		this.isAI = (this.rule && this.rule['variables']) ? this.rule['variables'].ref_ai : false;

		const sections = this.rule.uiSection.toLocaleLowerCase().split('|');

		if (sections.length) {
			this.ruleClass = 'rule-' + sections[0];

			if (sections.length > 1) {
				this.ruleClass += ' multiple-section-' + sections[1];
			}
		}

		this.ruleStatus = this.rule.status;
		this.updatePostRevisionBadges(this.rule.variables);
	}

	/**
	 * Toggles the expanded/collapsed state of the comment box
	 */
	toggleExpand(): void {
		this.isExpanded = !this.isExpanded;
	}

	/**
	 * Determines whether the "Show more / Show less" toggle should be displayed.
	 * @param note 
	 * @returns 
	 */
	shouldShowToggle(note: string): boolean {
		return note?.split('\n')?.length > 1 || note?.length > 150;
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onRuleSelected(): void {
		if (this.rule.multipleUiSections && this.rule.multipleUiSections.length <= 1) {
			const rule = Object.assign({}, this.rule);
			rule.uiSection = this.rule.multipleUiSections[0].uiSection;
			this.selected.emit(rule);
		}
	}

	/**
	 * On Rule selected over the Multiple section, emit the selected event with the current rule and the section.
	 */
	onMultipleRuleSelected(selectedUiSection: any): void {
		const rule = Object.assign({}, this.rule);
		rule.uiSection = selectedUiSection.uiSection;
		this.selected.emit(rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onViewComments(): void {
		this.viewComments.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onViewStips(): void {
		this.viewStips.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onAddComment(): void {
		this.addComment.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onAddStip(): void {
		this.addStip.emit(this.rule);
	}

	/**
	 * On update status click, call endpoint to update rule. Return the result through output event.
	 * @param status: RULE_STATUS
	 */
	onUpdateStatus(status: RULE_STATUS): void {
		if (this.viewOnlyUncheckedRules) {
			this.hideRule = true;
		}

		if (this.rule.status === 0) {
			this.broadcastService.broadcast({
				name: RULE_UPDATED_EVENT,
				event: {
					rule: this.rule,
					type: 'subtract'
				}
			});
		}

		this.ruleStatus = status;

		this.updating_status = status;

		const orderId = this.orderDetailModel.id;
		const documentId = this.orderDetailModel.documentId;
		this.ruleService
			.updateRuleStatus(orderId, documentId, this.rule.name, status)
			.subscribe(result => {
				if (result.isValid) {

					this.updateStatus.emit({
						ruleName: this.rule.name,
						ruleStatus: status
					}
					);

				} else {
					this.updateStatus.emit(false);
				}

				this.updating_status = null;
			});
	}

	/**
	 * Toggles the visibility of the rule feedback section.
	 */
	onFeedbackToggle() {
		this.isFeedbackOpen = !this.isFeedbackOpen;
	}

	/**
	 * Closes the rule feedback section.
	 */
	closeFeedback() {
		this.isFeedbackOpen = false;
	}

	/**
	 * Determines the icon if the button that toggles the feedback section.
	 * @returns {string} - The button icon class.
	 */
	getFeedBackIcon() {
		return this.isFeedbackOpen ? "fa-light fa-chevron-up" : "fa-light fa-lightbulb-exclamation";
	}

	/**
	 * Updates post-revision badges and confidence score based on the rules API response.
	 * @param apiResponse 
	 */
	updatePostRevisionBadges(variables: any): void {
		this.postRevisionBadges = buildPostRevisionBadges(variables);
		const score = Number(variables?.dispositionScore);

		this.aiConfidenceScore = !isNaN(score) ? score : undefined;
	}

	/**
	 * Returns true when we should refetch on expand.
	 * - No data yet
	 * - Previous request failed
	 * - Data exists but missing required fields
	 */
	private shouldRefetchPostRevision(): boolean {
		if (this.postRevisionLoadError) return true;
		if (!this.postRevisionDetails) return true;

		const hasComment = !!this.postRevisionDetails.revisionComment?.trim();
		const hasDate = !!this.postRevisionDetails.revisionDate?.trim();

		return !(hasComment && hasDate);
	}

	/**
	 * Called before the panel toggles. Triggers API call when expanding and refetch is needed.
	 */
	onPostRevisionBeforeToggle(event: any): void {
		const isCurrentlyCollapsed = event?.collapsed ?? this.postRevisionPanelCollapsed;
		const isExpanding = isCurrentlyCollapsed === true;
		if (!isExpanding) return;

		if (this.isPostRevisionLoading) return;
		if (!this.shouldRefetchPostRevision()) return;

		this.isPostRevisionLoading = true;
		this.postRevisionLoadError = false;

		const orderId = this.orderDetailModel.id;
		const documentId = this.orderDetailModel.documentId;
		const revisionId = this.rule?.variables?.revisionId;
		this.ruleService
			.getPostRevision(orderId, documentId, revisionId)
			.pipe(finalize(() => (this.isPostRevisionLoading = false)))
			.subscribe({
				next: (res) => {
					this.postRevisionDetails = res;

					// Mark as error if payload is missing required fields
					const ok = !!res?.revisionComment?.trim() && !!res?.revisionDate?.trim();
					this.postRevisionLoadError = !ok;
				},
				error: () => {
					this.postRevisionLoadError = true;
				},
			});
	}

	/**
	 * Keeps local collapsed state in sync with panel UI.
	 */
	onPostRevisionAfterToggle(event: any): void {
		this.postRevisionPanelCollapsed = event?.collapsed ?? this.postRevisionPanelCollapsed;
	}

	/**
	 * Manual retry button handler (optional).
	 */
	retryLoadPostRevision(): void {
		// Force refetch next time by setting state
		this.postRevisionLoadError = true;
		// If panel is already open, call immediately (optional)
		if (!this.postRevisionPanelCollapsed) {
			this.onPostRevisionBeforeToggle({ collapsed: true });
		}
	}

	/**
	 * Normalizes revisionComment for rendering.
	 * - If it contains HTML entities, decode once (or twice if needed).
	 * - Otherwise render as-is.
	 */
	normalizeRevisionComment(value?: string | null): string {
		const v = value ?? '';
		if (!v) return '';

		// Decode only when it looks encoded
		if (v.includes('&lt;') || v.includes('&gt;') || v.includes('&amp;')) {
			return decodeHtmlEntities(v, 2);
		}

		return v;
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
